import type { PageLinkSummary } from "@/lib/storefront-types";

export type DeliveryQuoteAddress = {
  id: string;
  label: string;
  address1: string;
  address2: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
};

export type DeliveryQuoteLookupResponse = {
  postcode: string;
  results: DeliveryQuoteAddress[];
};

const UK_POSTCODE_PATTERN =
  /^(GIR0AA|(?:[A-PR-UWYZ][0-9][0-9A-HJKSTUW]?|[A-PR-UWYZ][A-HK-Y][0-9][0-9ABEHMNPRVWXY]?)[0-9][ABD-HJLNP-UW-Z]{2})$/i;

function normalizePostcode(rawPostcode: string) {
  return rawPostcode.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function normalizeBoroughMatchValue(value: string) {
  return value
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/\b(?:the )?(?:royal |london )?borough of\b/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function isValidUkPostcode(rawPostcode: string) {
  const normalizedPostcode = normalizePostcode(rawPostcode);
  return normalizedPostcode.length > 0 && UK_POSTCODE_PATTERN.test(normalizedPostcode);
}

export function resolveBoroughLinkForAddress(
  address: DeliveryQuoteAddress,
  boroughLinks: PageLinkSummary[],
) {
  const normalizedProvince = normalizeBoroughMatchValue(address.province);
  if (!normalizedProvince) {
    return null;
  }

  return (
    boroughLinks.find(
      (link) => normalizeBoroughMatchValue(link.label) === normalizedProvince,
    ) ??
    boroughLinks.find((link) =>
      normalizeBoroughMatchValue(link.label).includes(normalizedProvince),
    ) ??
    boroughLinks.find((link) =>
      normalizedProvince.includes(normalizeBoroughMatchValue(link.label)),
    ) ??
    null
  );
}

export function buildDeliveryQuoteRedirectUrl(
  boroughPath: string,
  address: DeliveryQuoteAddress,
) {
  const params = new URLSearchParams({
    postcode: address.postalCode.trim(),
    addressLine1: address.address1.trim(),
  });

  return `${boroughPath}?${params.toString()}`;
}

export function formatDeliveryQuoteHeading(addressLine1: string, postcode: string) {
  return `Delivery options for ${addressLine1.trim()} ${postcode.trim()}`;
}
